import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Trash2, AlertTriangle } from "lucide-react";

export function DeleteAccountCard() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.delete("/auth/account", { data: { password, confirm } });
      await logout();
      toast.success("Your account and data have been deleted.");
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-6 bg-white rounded-3xl border border-red-200 p-6" data-testid="delete-account-card">
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-xl bg-red-50 flex items-center justify-center shrink-0"><AlertTriangle className="h-5 w-5 text-red-600" /></div>
        <div className="flex-1">
          <h2 className="font-bold text-navy">Delete my account</h2>
          <p className="text-sm text-muted-foreground mt-1">Permanently erase your Elite account and all its data — conversations, documents and favorites. This cannot be undone.</p>
          <Button variant="outline" data-testid="delete-account-open" onClick={() => setOpen(true)} className="mt-4 rounded-full border-red-300 text-red-600 hover:bg-red-50 hover:text-red-700">
            <Trash2 className="h-4 w-4 mr-1.5" /> Delete my account
          </Button>
        </div>
      </div>
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setPassword(""); setConfirm(""); } }}>
        <DialogContent className="max-w-md" data-testid="delete-account-dialog">
          <DialogHeader>
            <DialogTitle className="text-navy">Delete your account?</DialogTitle>
            <DialogDescription>All your conversations, documents and favorites will be permanently erased and you will be logged out.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div><Label htmlFor="del-pw">Your password</Label><Input id="del-pw" type="password" autoComplete="current-password" data-testid="delete-account-password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 h-11 rounded-xl" /></div>
            <div><Label htmlFor="del-confirm">Type <span className="font-bold">DELETE</span> to confirm</Label><Input id="del-confirm" data-testid="delete-account-confirm" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1.5 h-11 rounded-xl" /></div>
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="ghost" onClick={() => setOpen(false)} data-testid="delete-account-cancel" className="rounded-full">Cancel</Button>
              <Button type="submit" data-testid="delete-account-submit" disabled={busy || !password || confirm !== "DELETE"} className="rounded-full bg-red-600 hover:bg-red-700 text-white">
                {busy ? "Deleting…" : "Delete permanently"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
